var _dec, _dec2, _dec3, _class, _TopNavBarMenuItems, _span;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component } from 'react';
import { omitProps, safeCloneElement, withDeterministicId } from '@instructure/ui-react-utils';
import { warn, error } from '@instructure/console';
import { testable } from '@instructure/ui-testable';
import { withStyle } from '@instructure/emotion';
import { Drilldown } from '@instructure/ui-drilldown';
import { TruncateList } from '@instructure/ui-truncate-list';
import { TopNavBarContext } from '../TopNavBarContext';
import { TopNavBarItem } from '../TopNavBarItem';
import { mapItemsForDrilldown, renderMappedItemDrilldownSubpages, renderMappedItemsAsDrilldownOptions } from '../utils/mapItemsForDrilldown';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createElement as _createElement } from "@emotion/react";
/**
---
parent: TopNavBar
id: TopNavBar.MenuItems
---
@module TopNavBarMenuItems
**/
let TopNavBarMenuItems = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_TopNavBarMenuItems = class TopNavBarMenuItems extends Component {
  constructor(props) {
    super(props);
    this._hiddenMenuItemsMenuTriggerId = void 0;
    this._hiddenItemsMenuId = void 0;
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.renderOptionContent = (children, itemProps) => {
      const styles = this.props.styles;
      return _jsx("span", {
        css: (itemProps === null || itemProps === void 0 ? void 0 : itemProps.status) === 'active' ? styles === null || styles === void 0 ? void 0 : styles.submenuOptionActive : styles === null || styles === void 0 ? void 0 : styles.submenuOption,
        children: children
      });
    };
    this._hiddenMenuItemsMenuTriggerId = props.deterministicId('TopNavBarMenuItems-hiddenMenuItemsMenuTrigger');
    this._hiddenItemsMenuId = props.deterministicId('TopNavBarSmallViewportLayout-drilldown');
    this.state = {
      key: 0,
      visibleItemsCount: void 0
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
    if (Children.count(prevProps.children) !== this.childrenArray.length) {
      this.setState({
        key: this.state.key + 1
      });
    }
  }
  get childrenArray() {
    return Children.toArray(this.props.children);
  }
  renderHiddenItemsMenu(hiddenItems) {
    const _this$props3 = this.props,
      renderHiddenItemsMenuTriggerLabel = _this$props3.renderHiddenItemsMenuTriggerLabel,
      currentPageId = _this$props3.currentPageId;
    if (!hiddenItems.length) {
      return _span || (_span = _jsx("span", {}));
    }
    const mappedItems = mapItemsForDrilldown(hiddenItems, {
      renderOptionContent: this.renderOptionContent,
      currentPageId
    });
    const subPages = renderMappedItemDrilldownSubpages(mappedItems);
    const options = renderMappedItemsAsDrilldownOptions(mappedItems);
    const hasActiveChild = hiddenItems.find(child => child.props.status === 'active');
    return _jsx(TopNavBarItem, {
      id: this._hiddenMenuItemsMenuTriggerId,
      status: hasActiveChild ? 'active' : 'default',
      renderSubmenu: _jsx(Drilldown, {
        shouldSetAriaExpanded: false,
        rootPageId: this._hiddenItemsMenuId,
        children: [_jsx(Drilldown.Page, {
          id: this._hiddenItemsMenuId,
          children: options
        }, this._hiddenItemsMenuId), ...subPages]
      }),
      children: renderHiddenItemsMenuTriggerLabel(hiddenItems.length)
    });
  }
  renderChildren() {
    const currentPageId = this.props.currentPageId;
    return this.childrenArray.map(child => {
      if (!child) {
        return;
      }
      const _child$props = child.props,
        id = _child$props.id,
        status = _child$props.status,
        variant = _child$props.variant,
        renderSubmenu = _child$props.renderSubmenu,
        renderAvatar = _child$props.renderAvatar;
      const isCurrentPage = currentPageId === id;
      if (renderAvatar) {
        error(false, `Items in <TopNavBar.MenuItems> are not allowed to have avatars, but item with id: "${id}" has \`renderAvatar\` prop.`);
        return null;
      }
      if (!isCurrentPage) {
        return child;
      } else {
        if (variant !== 'default') {
          warn(false, `Only \`variant="default"\` items can be set to current/active, but the item with id "${id}" is "${variant}" variant.`);
          return child;
        }
        if (status === 'disabled') {
          warn(false, `Disabled items can not be set to current/active, but the item with id "${id}" is disabled.`);
          return child;
        }
        return safeCloneElement(child, {
          status: 'active',
          // if it has submenu, the root item cannot be current,
          // just a link inside the submenu
          'aria-current': renderSubmenu ? void 0 : 'page'
        });
      }
    });
  }
  render() {
    const _this$props4 = this.props,
      listLabel = _this$props4.listLabel,
      styles = _this$props4.styles;
    if (!this.childrenArray.length) {
      return null;
    }
    if (this.context.layout === 'smallViewport') {
      // in smallViewport mode it is rendered as a Drilldown
      return null;
    }
    return _createElement(TruncateList, {
      ...omitProps(this.props, allowedProps),
      key: this.state.key // rerender if child count changes
      ,
      elementRef: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.topNavBarMenuItems,
      visibleItemsCount: this.state.visibleItemsCount,
      onUpdate: ({
        visibleItemsCount
      }) => {
        this.setState({
          visibleItemsCount
        });
      },
      renderHiddenItemMenu: hiddenChildren => this.renderHiddenItemsMenu(hiddenChildren),
      ...((styles === null || styles === void 0 ? void 0 : styles.itemSpacing) && {
        itemSpacing: styles.itemSpacing
      }),
      "aria-label": listLabel
    }, this.renderChildren());
  }
}, _TopNavBarMenuItems.displayName = "TopNavBarMenuItems", _TopNavBarMenuItems.componentId = 'TopNavBar.MenuItems', _TopNavBarMenuItems.propTypes = propTypes, _TopNavBarMenuItems.allowedProps = allowedProps, _TopNavBarMenuItems.defaultProps = {}, _TopNavBarMenuItems.contextType = TopNavBarContext, _TopNavBarMenuItems)) || _class) || _class) || _class);
export { TopNavBarMenuItems };
export default TopNavBarMenuItems;